import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { MarketSubNav } from '../components/shared/MarketSubNav'
import { Card, Pill, DataTable } from '../components/ui'
import type { Column } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror GET /api/v1/rivals/gaps) ──────────────────────────────────
interface Location {
  id: string
  name: string
}
interface GapRow {
  key: string
  name: string
  brand: string | null
  category: string
  rivals: number
  avg_price: number
  min_price: number
  max_price: number
  opportunity: 'high' | 'medium' | 'low'
  change?: 'new' | 'growing' | 'shrinking' | 'stable' | 'baseline'
  rival_change?: number | null
}
interface GapData {
  location: { id: string; name: string }
  own_menu: boolean
  tracked_rivals: number
  summary: {
    gap_skus_total: number
    high_opportunity: number
    medium_opportunity: number
    low_opportunity: number
    new_since_prior?: number
    growing_since_prior?: number
    shrinking_since_prior?: number
    resolved_since_prior?: number
  }
  categories: string[]
  gaps: GapRow[]
  truncated: boolean
  previous_collected_at?: string | null
}

// ─── Formatters (user-local) ─────────────────────────────────────────────────
const fmtMoney = (n: number) => `$${n.toFixed(2)}`
const fmtInt = (n: number) => n.toLocaleString('en-US')

// Opportunity → label + color. `high` (many rivals carry it, you don't) is the
// strongest signal → warm; medium → accent; low → muted.
const OPP_META: Record<GapRow['opportunity'], { label: string; color: string }> = {
  high: { label: 'High', color: 'var(--warm)' },
  medium: { label: 'Medium', color: 'var(--accent)' },
  low: { label: 'Low', color: 'var(--text-3)' },
}

// "Carried by" thresholds — filter gap rows by how many rivals stock them.
type CarriedBy = 'any' | '2+' | '3+' | 'all'
const CARRIED_OPTIONS: { key: CarriedBy; label: string }[] = [
  { key: 'any', label: 'Any rival' },
  { key: '2+', label: '2+ rivals' },
  { key: '3+', label: '3+ rivals' },
  { key: 'all', label: 'All rivals' },
]

type SortCol = 'name' | 'brand' | 'category' | 'rivals' | 'avg_price' | 'opportunity'

// ─── Component ───────────────────────────────────────────────────────────────
export default function SkuGapAnalysis() {
  const authFetch = useAuthFetch()

  // Deep-link params (e.g. from Catalog Comparison's "Review <category> gaps"
  // action). Honored ONCE on first load, and only when truthful: an unknown
  // location falls back to the default, and a category not present in the
  // returned gap set falls back to "All Categories" — never a dead filter.
  const [searchParams] = useSearchParams()
  const requestedLocation = searchParams.get('location_id')
  const requestedCategory = searchParams.get('category')
  const paramsAppliedRef = useRef(false)

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [data, setData] = useState<GapData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [catFilter, setCatFilter] = useState<string>('All Categories')
  const [carriedBy, setCarriedBy] = useState<CarriedBy>('any')
  const [locOpen, setLocOpen] = useState(false)
  const [catOpen, setCatOpen] = useState(false)
  const [byOpen, setByOpen] = useState(false)

  const [sortCol, setSortCol] = useState<SortCol>('rivals')
  const [sortDir, setSortDir] = useState<1 | -1>(-1)

  // Queue paging — the page opens as a short "work-from-the-top" slice, not a
  // wall of every gap SKU. The user extends the queue deliberately.
  const PAGE = 25
  const [visibleCount, setVisibleCount] = useState(PAGE)

  // The row the operator is reviewing. Clicking a row / its action button opens
  // a persistent rival-pricing review panel (below) — the next step on a gap.
  const [selectedGap, setSelectedGap] = useState<GapRow | null>(null)
  const [shortlist, setShortlist] = useState<string[]>([])

  // Load the org's locations, default to the first.
  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then((r) => r.json())
      .then((d: { locations?: Location[] }) => {
        const locs = d.locations ?? []
        setLocations(locs)
        if (locs.length > 0) {
          const preferred = requestedLocation && locs.some((l) => l.id === requestedLocation) ? requestedLocation : locs[0].id
          setSelectedLocation((cur) => cur || preferred)
        } else setLoading(false)
      })
      .catch(() => setError('Could not load your locations.'))
  }, [authFetch, requestedLocation])

  const load = useCallback(() => {
    if (!selectedLocation) return
    setLoading(true); setError(null)
    authFetch(`${API}/api/v1/rivals/gaps?location_id=${selectedLocation}`)
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
        return r.json()
      })
      .then((d: { data: GapData }) => {
        setData(d.data)
        // Apply a deep-linked category ONCE, and only if it's really in this
        // location's gap set; otherwise show everything (no dead/false filter).
        const cats = d.data.categories ?? []
        const applyCat = !paramsAppliedRef.current && requestedCategory && cats.includes(requestedCategory)
        setCatFilter(applyCat ? requestedCategory! : 'All Categories')
        setCarriedBy('any')
        paramsAppliedRef.current = true
      })
      .catch((e) => setError(e?.message || 'Network error'))
      .finally(() => setLoading(false))
  }, [authFetch, selectedLocation, requestedCategory])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!selectedLocation) return
    try {
      const saved = localStorage.getItem(`cs-buying-shortlist:${selectedLocation}`)
      setShortlist(saved ? JSON.parse(saved) : [])
    } catch {
      setShortlist([])
    }
  }, [selectedLocation])

  const selectedName = useMemo(
    () => locations.find((l) => l.id === selectedLocation)?.name ?? data?.location.name ?? '—',
    [locations, selectedLocation, data]
  )

  const allGaps = data?.gaps ?? []
  const trackedRivals = data?.tracked_rivals ?? 0

  // Min-rivals threshold for the "Carried by" filter. `all` means every tracked
  // rival with data carries it — anchored to the real tracked count.
  const minRivals = useMemo(() => {
    switch (carriedBy) {
      case '2+': return 2
      case '3+': return 3
      case 'all': return Math.max(1, trackedRivals)
      default: return 1
    }
  }, [carriedBy, trackedRivals])

  const filteredGaps = useMemo(() => {
    let rows = allGaps.filter((g) => g.rivals >= minRivals)
    if (catFilter !== 'All Categories') rows = rows.filter((g) => g.category === catFilter)
    const dir = sortDir
    return [...rows].sort((a, b) => {
      const va = a[sortCol]
      const vb = b[sortCol]
      if (sortCol === 'opportunity') {
        const rank = { high: 3, medium: 2, low: 1 }
        return (rank[a.opportunity] - rank[b.opportunity]) * dir
      }
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir
      return ((va as number) - (vb as number)) * dir
    })
  }, [allGaps, minRivals, catFilter, sortCol, sortDir])

  // Any change to what/how the queue is ordered restarts it from the top slice.
  useEffect(() => { setVisibleCount(PAGE); setSelectedGap(null) }, [catFilter, carriedBy, selectedLocation, sortCol, sortDir])

  function handleSort(col: SortCol) {
    if (col === sortCol) setSortDir((d) => (d === 1 ? -1 : 1))
    else { setSortCol(col); setSortDir(-1) }
  }
  const sortIndicator = (col: SortCol) => (col === sortCol ? (sortDir === 1 ? ' ▲' : ' ▼') : '')

  const sum = data?.summary
  const hasGaps = allGaps.length > 0

  // ── Priority frame (all real) ────────────────────────────────────────────
  // The ranking rule is REAL and comes from the API: opportunity is derived
  // from rival coverage (3+ rivals → high · 2 → medium · 1 → low), and gaps
  // arrive sorted strongest-first (rivals desc, then avg price). So allGaps[0]
  // is the location's single strongest add — no invented score.
  const highCount = sum?.high_opportunity ?? 0
  const medCount = sum?.medium_opportunity ?? 0
  const totalCount = sum?.gap_skus_total ?? 0
  const topAdd = allGaps[0]
  const newCount = sum?.new_since_prior ?? 0
  const growingCount = sum?.growing_since_prior ?? 0
  const resolvedCount = sum?.resolved_since_prior ?? 0
  const hasPrior = !!data?.previous_collected_at

  const toggleShortlist = (gap: GapRow) => {
    setShortlist((current) => {
      const next = current.includes(gap.key) ? current.filter((key) => key !== gap.key) : [...current, gap.key]
      if (selectedLocation) localStorage.setItem(`cs-buying-shortlist:${selectedLocation}`, JSON.stringify(next))
      return next
    })
  }

  const exportShortlist = () => {
    const rows = allGaps.filter((gap) => shortlist.includes(gap.key))
    if (rows.length === 0) return
    const quote = (value: string | number | null) => `"${String(value ?? '').replace(/"/g, '""')}"`
    const csv = [
      ['SKU', 'Brand', 'Category', 'Rivals carrying', 'Average rival price'].map(quote).join(','),
      ...rows.map((gap) => [gap.name, gap.brand, gap.category, gap.rivals, gap.avg_price].map(quote).join(',')),
    ].join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `cannaspy-buying-shortlist-${selectedLocation}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const changeMeta = (gap: GapRow) => {
    if (gap.change === 'new') return { label: 'New gap', color: 'var(--danger)', tone: 'danger' as const }
    if (gap.change === 'growing') return { label: `+${Math.abs(gap.rival_change ?? 0)} rival`, color: 'var(--warm)', tone: 'warning' as const }
    if (gap.change === 'shrinking') return { label: `−${Math.abs(gap.rival_change ?? 0)} rival`, color: 'var(--accent)', tone: 'accent' as const }
    if (gap.change === 'stable') return { label: 'Stable', color: 'var(--text-3)', tone: 'neutral' as const }
    return { label: 'Baseline', color: 'var(--text-3)', tone: 'neutral' as const }
  }

  // The visible queue, split into the immediate-add head (top 5) and the rest.
  // Both slices are taken AFTER ranking/sort, so order is never changed here.
  const visibleGaps = filteredGaps.slice(0, visibleCount)
  const headGaps = visibleGaps.slice(0, 5)
  const restGaps = visibleGaps.slice(5)

  // ── Gap table columns ─────────────────────────────────────────────────────
  const columns: Column<GapRow>[] = [
    {
      key: 'name', width: '27%', header: <span onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>SKU{sortIndicator('name')}</span>,
      mono: false, render: (r) => <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{r.name}</span>,
    },
    {
      key: 'brand', width: '14%', header: <span onClick={() => handleSort('brand')} style={{ cursor: 'pointer' }}>Brand{sortIndicator('brand')}</span>,
      mono: false, render: (r) => <span style={{ color: 'var(--text-2)' }}>{r.brand ?? '—'}</span>,
    },
    {
      key: 'category', width: '12%', header: <span onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>Category{sortIndicator('category')}</span>,
      mono: false, render: (r) => <span style={{ color: 'var(--text-3)' }}>{r.category}</span>,
    },
    {
      // The demand column carries BOTH the count and the tier signal (color),
      // so a separate "Opportunity" pill would just repeat it. Color maps to the
      // legend above: 3+ rivals → High (warm) · 2 → Med (accent) · 1 → Low (muted).
      key: 'rivals', width: '13%', header: <span onClick={() => handleSort('rivals')} style={{ cursor: 'pointer' }}>Rivals Carrying{sortIndicator('rivals')}</span>,
      render: (r) => {
        const c = OPP_META[r.opportunity].color
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} style={{ width: 10, height: 10, borderRadius: 2, marginRight: 2, background: i < r.rivals ? c : 'var(--surface-3)' }} />
            ))}
            <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: c, marginLeft: 4 }}>{r.rivals}</span>
          </div>
        )
      },
      align: 'right',
    },
    {
      key: 'avg_price', width: '11%', header: <span onClick={() => handleSort('avg_price')} style={{ cursor: 'pointer' }}>Avg Rival Price{sortIndicator('avg_price')}</span>,
      align: 'right', render: (r) => <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{fmtMoney(r.avg_price)}</span>,
    },
    {
      key: 'change', width: '11%', header: 'Since prior', mono: false,
      render: (r) => {
        const meta = changeMeta(r)
        return <Pill tone={meta.tone} variant="chip">{meta.label}</Pill>
      },
    },
  ]

  // A separate shortlist action turns the ranked evidence into a buying task.
  // Row click still opens pricing evidence; the button stops propagation so
  // adding an item never unexpectedly opens the detail panel.
  const actionColumn: Column<GapRow> = {
    key: 'action', width: '12%',
    header: <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>Buying list</span>,
    mono: false, align: 'right',
    render: (r) => {
      const added = shortlist.includes(r.key)
      return (
        <button
          type="button"
          aria-label={`${added ? 'Remove' : 'Add'} ${r.name} ${added ? 'from' : 'to'} buying shortlist`}
          onClick={(event) => { event.stopPropagation(); toggleShortlist(r) }}
          style={{
            border: `1px solid ${added ? 'var(--accent)' : 'var(--border-2)'}`, borderRadius: 'var(--r-xs)',
            background: added ? 'var(--accent-soft)' : 'var(--surface)', color: added ? 'var(--accent)' : 'var(--text-2)',
            padding: '4px 7px', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 10.5, fontWeight: 750,
          }}
        >
          {added ? '✓ Added' : '+ Shortlist'}
        </button>
      )
    },
  }
  const gapColumns = [...columns, actionColumn]

  // Comfortable density, matched cell-for-cell to the shared DataTable so a
  // CLOSED row here is pixel-identical to every other table in the product.
  const ROW_H = 34, CELL_PAD = 8, HEAD_PAD = 7
  // Row-level open signals only — a faint accent tint on the row's own ground
  // plus a 3px left bar drawn as an inset shadow (no layout shift, no border
  // that would read as a box edge). Both lines of an open row share them, so the
  // pair reads as ONE row that grew, not a row followed by a panel.
  const OPEN_TINT = 'color-mix(in srgb, var(--accent) 7%, var(--surface))'
  const ACCENT_BAR = 'inset 3px 0 0 var(--accent)'

  // The open row's second line: one plain sentence, the average emphasised
  // inside it. No SKU / brand / category / rivals restated — line 1 already
  // carries the row's identity, and repeating it is what made the old strip feel
  // bolted on.
  const detailSentence = (g: GapRow) => (
    <span style={{ color: 'var(--text-2)', fontSize: 12 }}>
      Rivals charge{' '}
      <strong style={{ color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>{fmtMoney(g.min_price)}–{fmtMoney(g.max_price)}</strong>. Average{' '}
      <strong style={{ color: 'var(--accent)', fontFamily: 'var(--mono)', fontSize: 14 }}>{fmtMoney(g.avg_price)}</strong>.
    </span>
  )

  // Render a queue zone as a SINGLE table (DataTable can't host an expandable
  // row, so the zone is rendered locally). The clicked row becomes a two-line
  // row in place: line 1 is its normal cells, line 2 is a full-width sub-row
  // that shares its tint + left bar with NO border between them. Clicking the
  // same row (or its action) collapses it — there is no standalone close.
  const renderQueueZone = (rows: GapRow[]) => {
    if (rows.length === 0) {
      return (
        <div style={{ padding: '16px 12px', textAlign: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 11 }}>
          No gap SKUs match these filters.
        </div>
      )
    }
    return (
      <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 11.5 }}>
        <thead>
          <tr>
            {gapColumns.map((c) => (
              <th
                key={c.key}
                className={`col-${c.key}`}
                style={{
                  fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'var(--text-2)', whiteSpace: 'nowrap',
                  position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface-solid)',
                  borderBottom: '2px solid var(--border-2)', padding: `${HEAD_PAD}px 12px`,
                  textAlign: c.align ?? 'left', width: c.width, minWidth: c.minWidth, userSelect: 'none',
                }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  width: c.align === 'right' || c.align === 'center' ? '100%' : undefined,
                  justifyContent: c.align === 'right' ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start',
                }}>
                  {c.header}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const open = selectedGap?.key === r.key
            const isLast = i === rows.length - 1
            const toggle = () => setSelectedGap(open ? null : r)
            return (
              <Fragment key={r.key}>
                <tr onClick={toggle} style={{ cursor: 'pointer', background: open ? OPEN_TINT : undefined }}>
                  {gapColumns.map((c, ci) => {
                    const mono = c.mono ?? ci !== 0
                    return (
                      <td
                        key={c.key}
                        className={`cell-${c.key}${mono ? ' tnum' : ''}`}
                        style={{
                          height: ROW_H, padding: `${CELL_PAD}px 12px`, color: 'var(--text-1)',
                          // Open line 1 drops its bottom border so it flows into line 2.
                          borderBottom: open ? 'none' : isLast ? 'none' : '1px solid var(--border)',
                          fontFamily: mono ? 'var(--mono)' : 'var(--sans)', textAlign: c.align ?? 'left',
                          // Fixed layout gives every cell its exact column width; keep
                          // content on one line so a long SKU clips instead of wrapping
                          // (wrapping would vary row height, not the column grid).
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          boxShadow: open && ci === 0 ? ACCENT_BAR : undefined,
                        }}
                      >
                        {c.render?.(r, i) ?? null}
                      </td>
                    )
                  })}
                </tr>
                {open && (
                  <tr style={{ background: OPEN_TINT }}>
                    <td
                      colSpan={gapColumns.length}
                      style={{
                        padding: '1px 16px 9px 12px', lineHeight: 1.4, boxShadow: ACCENT_BAR,
                        borderBottom: isLast ? 'none' : '1px solid var(--border)',
                      }}
                    >
                      {detailSentence(r)}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    )
  }

  // Dropdown chrome (shared with s22/s23 conventions).
  const ddBtn = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 'var(--r-sm)',
    fontSize: 11, fontWeight: 600, border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border-2)'}`,
    background: active ? 'var(--accent)' : 'var(--surface)', color: active ? '#fff' : 'var(--text-1)',
    cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
  })
  const ddPop: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 5px)', left: 0, background: 'var(--surface)',
    border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', boxShadow: '0 8px 32px rgba(30,60,80,0.14)',
    zIndex: 300, minWidth: 160, padding: 5, maxHeight: 280, overflowY: 'auto',
  }
  const ddOpt = (sel: boolean): React.CSSProperties => ({
    padding: '7px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
    color: sel ? 'var(--accent)' : 'var(--text-1)', fontWeight: sel ? 700 : 400, whiteSpace: 'nowrap',
  })

  const catOptions = ['All Categories', ...(data?.categories ?? [])]

  return (
    <div
      onClick={() => { setLocOpen(false); setCatOpen(false); setByOpen(false) }}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', fontFamily: 'var(--sans)', color: 'var(--text-1)', fontSize: 14, overflow: 'hidden' }}
    >
      {/* TOPBAR */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface)' }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>SKU Gap Analysis</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase' }}>
            Products you should consider adding
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {data && hasGaps && (
            <Pill tone="neutral" variant="chip" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {fmtInt(sum?.gap_skus_total ?? 0)} gap SKUs · {fmtInt(trackedRivals)} tracked rival{trackedRivals === 1 ? '' : 's'}
            </Pill>
          )}
        </div>
      </div>

      {/* MARKET NAV TABS */}
      <MarketSubNav />

      {/* FILTER BAR */}
      <div onClick={(e) => e.stopPropagation()} style={{ padding: '8px 28px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Location</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div style={ddBtn(true)} onClick={(e) => { e.stopPropagation(); setLocOpen((o) => !o); setCatOpen(false); setByOpen(false) }}>
            {selectedName}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {locOpen && (
            <div style={ddPop}>
              {locations.length === 0
                ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No locations yet</div>
                : locations.map((l) => (
                  <div key={l.id} style={ddOpt(l.id === selectedLocation)}
                    onClick={() => { setSelectedLocation(l.id); setLocOpen(false) }}>
                    {l.name}{l.id === selectedLocation ? ' ✓' : ''}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px', flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Category</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div data-testid="gap-cat-btn" style={ddBtn(catFilter !== 'All Categories')} onClick={(e) => { e.stopPropagation(); setCatOpen((o) => !o); setLocOpen(false); setByOpen(false) }}>
            {catFilter}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {catOpen && (
            <div data-testid="gap-cat-pop" style={ddPop}>
              {catOptions.length <= 1
                ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No categories yet</div>
                : catOptions.map((opt) => (
                  <div key={opt} style={ddOpt(opt === catFilter)}
                    onClick={() => { setCatFilter(opt); setCatOpen(false) }}>
                    {opt}{opt === catFilter ? ' ✓' : ''}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px', flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Carried by</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div style={ddBtn(carriedBy !== 'any')} onClick={(e) => { e.stopPropagation(); setByOpen((o) => !o); setLocOpen(false); setCatOpen(false) }}>
            {CARRIED_OPTIONS.find((o) => o.key === carriedBy)?.label}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {byOpen && (
            <div style={ddPop}>
              {CARRIED_OPTIONS.map((o) => (
                <div key={o.key} style={ddOpt(o.key === carriedBy)}
                  onClick={() => { setCarriedBy(o.key); setByOpen(false) }}>
                  {o.label}{o.key === carriedBy ? ' ✓' : ''}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
          {data && hasGaps
            ? catFilter === 'All Categories' && carriedBy === 'any'
              ? <><span style={{ color: 'var(--danger)', fontWeight: 700 }}>{fmtInt(totalCount)} SKU{totalCount === 1 ? '' : 's'}</span> rivals carry that you don't</>
              : <><span style={{ color: 'var(--danger)', fontWeight: 700 }}>{fmtInt(filteredGaps.length)} matching SKU{filteredGaps.length === 1 ? '' : 's'}</span> in the returned set</>
            : ''}
        </div>
      </div>

      {/* MAIN SCROLL */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px', minHeight: 0 }}>

        {/* ACTION FRAME — what this list is, why it's ranked, what to do first */}
        {!error && hasGaps && (
          <Card data-testid="gap-summary" variant="sm" style={{ padding: 0, overflow: 'hidden', marginBottom: 16, borderLeft: '3px solid var(--warm)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(320px, 0.75fr)' }}>
              <div style={{ padding: '20px 22px', background: 'linear-gradient(115deg, var(--warm-soft), transparent 68%)' }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--warm)', marginBottom: 8 }}>What changed</div>
                <div style={{ fontSize: 25, fontWeight: 800, color: 'var(--text-1)', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
                  {hasPrior ? `${fmtInt(newCount)} new assortment gaps need review` : `${fmtInt(totalCount)} assortment gaps need review`}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                  {hasPrior ? (
                    <>
                      <Pill tone="danger" variant="chip">{fmtInt(newCount)} NEW</Pill>
                      <Pill tone="warning" variant="chip">{fmtInt(growingCount)} GROWING</Pill>
                      <Pill tone="accent" variant="chip">{fmtInt(resolvedCount)} RESOLVED</Pill>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text-3)' }}>since the prior rival scan</span>
                    </>
                  ) : <Pill tone="neutral" variant="chip">NEW BASELINE</Pill>}
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-2)', marginTop: 11 }}>
                  <strong style={{ color: 'var(--text-1)' }}>{fmtInt(highCount)} high-priority gaps</strong> are carried by at least three of your {fmtInt(trackedRivals)} tracked rivals. Rival coverage is evidence of availability—not proof of sales demand.
                </div>
              </div>

              {topAdd && (
                <div style={{ padding: '20px 22px', borderLeft: '1px solid var(--border-2)', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent-hover)', marginBottom: 8 }}>Recommended next step</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-1)', lineHeight: 1.25 }}>Shortlist {topAdd.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5, marginTop: 7 }}>
                    {topAdd.rivals} rivals carry it at an average of <b style={{ color: 'var(--text-1)' }}>{fmtMoney(topAdd.avg_price)}</b>. {hasPrior ? `${changeMeta(topAdd).label} since the prior scan.` : 'This scan establishes the comparison baseline.'}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleShortlist(topAdd)}
                    style={{ marginTop: 13, padding: '9px 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: shortlist.includes(topAdd.key) ? 'var(--accent-soft)' : 'var(--accent)', color: shortlist.includes(topAdd.key) ? 'var(--accent)' : '#fff', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 800 }}
                  >
                    {shortlist.includes(topAdd.key) ? '✓ Added to buying shortlist' : '+ Add to buying shortlist'}
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '12px 20px', borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
              {[[highCount, 'Stock first', '3+ rivals'], [medCount, 'Consider next', '2 rivals'], [totalCount, 'Total gaps', 'full list']].map(([value, label, sub], index) => (
                <div key={String(label)} style={{ minWidth: 125, padding: '0 18px', borderRight: index < 2 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 19, fontWeight: 750, color: index === 0 ? 'var(--warm)' : index === 1 ? 'var(--accent)' : 'var(--text-2)', lineHeight: 1 }}>{fmtInt(Number(value))}</div>
                  <div style={{ fontSize: 10.5, fontWeight: 750, color: 'var(--text-1)', marginTop: 4 }}>{label}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>
                </div>
              ))}
              {shortlist.length > 0 && (
                <button onClick={exportShortlist} style={{ marginLeft: 'auto', padding: '7px 11px', border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', color: 'var(--text-1)', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 750 }}>
                  Export shortlist ({shortlist.length})
                </button>
              )}
            </div>
          </Card>
        )}

        {/* ERROR */}
        {error && (
          <Card variant="sm" style={{ padding: '18px 20px', borderColor: 'var(--danger-soft)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)', marginBottom: 6 }}>Couldn't load your SKU gaps.</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginBottom: 12 }}>{error}</div>
            <button onClick={load} style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Retry</button>
          </Card>
        )}

        {/* HONEST DEGRADE — no own menu / no tracked rivals / nothing missing */}
        {!error && !loading && data && !hasGaps && (
          <Card variant="sm" style={{ padding: '18px 20px' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>No gaps to show</div>
            <div style={{ fontSize: 13, color: 'var(--text-1)', fontWeight: 600, marginBottom: 6 }}>
              {!data.own_menu
                ? `Your own menu isn't in range yet for ${selectedName}.`
                : trackedRivals === 0
                  ? `No tracked rivals at ${selectedName} yet.`
                  : `You already carry everything your tracked rivals do at ${selectedName}.`}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {!data.own_menu
                ? 'We haven’t collected your own menu for this location, so we can’t tell what you’re missing. Once it’s in range, the products your rivals carry that you don’t will appear here.'
                : trackedRivals === 0
                  ? 'Add a rival to your tracking set at this location to see the products they carry that you don’t.'
                  : 'Every SKU your tracked rivals stock has a match on your own menu — no gaps right now.'}
            </div>
          </Card>
        )}

        {/* GAP TABLE */}
        {!error && (loading || hasGaps) && (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              <span style={{ fontSize: 12, fontWeight: 700 }}>Add-next list — ranked by rival demand</span>
              {/* Legend — decodes the color of the Rivals Carrying column on-page so a
                  founder can see WHY one row outranks another without leaving the screen. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 14 }}>
                {[
                  { label: 'High', sub: '3+ rivals', color: 'var(--warm)' },
                  { label: 'Med', sub: '2 rivals', color: 'var(--accent)' },
                  { label: 'Low', sub: '1 rival', color: 'var(--text-3)' },
                ].map((t) => (
                  <span key={t.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text-3)' }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: t.color }} />
                    <span style={{ color: t.color, fontWeight: 700 }}>{t.label}</span>
                    <span>= {t.sub}</span>
                  </span>
                ))}
              </div>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>
                {data?.truncated ? `Top ${fmtInt(allGaps.length)} of ${fmtInt(sum?.gap_skus_total ?? 0)} · Compare prices on any gap` : 'Compare prices on any gap'}
              </span>
            </div>

            {loading && !hasGaps ? (
              // First load — single table showing the loading state.
              <DataTable
                columns={gapColumns}
                rows={[]}
                rowKey={(r) => r.key}
                empty={<span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>Pulling your menu and rival prices…</span>}
              />
            ) : (
              <>
                {/* ZONE 1 — immediate adds. Accent-tinted band + strong subhead so
                    the 5 strongest gaps read as "do these first", not row 1 of many. */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '9px 16px', background: 'color-mix(in srgb, var(--accent) 7%, var(--surface))', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-0.01em' }}>Start with these</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                    Your {headGaps.length} strongest gap{headGaps.length === 1 ? '' : 's'} — add first
                  </span>
                </div>
                {renderQueueZone(headGaps)}

                {/* ZONE 2 — the continuing queue, visibly secondary to the head. */}
                {restGaps.length > 0 && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '8px 16px', background: 'var(--surface-2)', borderTop: '1px solid var(--border-2)', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>Then work down the queue</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text-3)' }}>
                        rows 6–{fmtInt(5 + restGaps.length)}
                      </span>
                    </div>
                    {renderQueueZone(restGaps)}
                  </>
                )}
              </>
            )}

            {/* QUEUE FOOTER — extend the working slice deliberately, top-down. */}
            {!loading && filteredGaps.length > PAGE && (
              <div style={{ padding: '11px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: 'var(--surface-2)' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--text-3)' }}>
                  Showing <strong style={{ color: 'var(--text-1)' }}>{fmtInt(Math.min(visibleCount, filteredGaps.length))}</strong> of {fmtInt(filteredGaps.length)} in queue
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  {visibleCount < filteredGaps.length ? (
                    <>
                      <button
                        onClick={() => setVisibleCount((v) => Math.min(v + PAGE, filteredGaps.length))}
                        style={{ fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 700, padding: '6px 14px', borderRadius: 'var(--r-sm)', cursor: 'pointer', border: '1.5px solid var(--accent)', background: 'var(--accent)', color: '#fff' }}
                      >
                        Show {fmtInt(Math.min(PAGE, filteredGaps.length - visibleCount))} more
                      </button>
                      <button
                        onClick={() => setVisibleCount(filteredGaps.length)}
                        style={{ fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 700, padding: '6px 14px', borderRadius: 'var(--r-sm)', cursor: 'pointer', border: '1.5px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-1)' }}
                      >
                        Show all {fmtInt(filteredGaps.length)}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setVisibleCount(PAGE)}
                      style={{ fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 700, padding: '6px 14px', borderRadius: 'var(--r-sm)', cursor: 'pointer', border: '1.5px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-1)' }}
                    >
                      Show fewer
                    </button>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

    </div>
  )
}
